/**
 * `.gproj` の開保存と FASTA / GenBank 書き出し（I05 / I30 / I31）。
 * 配列バイトは IndexedDB にだけ置く。
 */

import {
  DEFAULT_CHUNK_SIZE,
  formatFastaText,
  formatGenBankText,
  iterateBlobBytes,
  readGprojStream,
  writeGprojStream,
  type GenomicFeatureV1,
  type GprojManifest,
} from '@kensuke/genome-core';
import {
  clearProject,
  getChunk,
  getFeatures,
  importStagingProjectId,
  listSequenceMeta,
  promoteImportStaging,
  putChunks,
  putFeatures,
  putSequenceMeta,
  type StoredSequenceMeta,
} from '../services/geneAnalysisChunkStore.service';

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function openGprojFile(projectId: string, file: File): Promise<GprojManifest> {
  const stagingId = importStagingProjectId(projectId);
  await clearProject(stagingId);
  const pendingMeta = new Map<string, { name: string; description: string; topology: 'linear' | 'circular'; chunkSize: number }>();
  const featureBag = new Map<string, GenomicFeatureV1[]>();
  const chunkBag = new Map<string, Array<{ index: number; codes: Uint8Array }>>();

  const result = await readGprojStream(iterateBlobBytes(file), {
    onManifest: (manifest) => {
      for (const entry of manifest.sequences) {
        pendingMeta.set(entry.id, {
          name: entry.name,
          description: entry.description ?? '',
          topology: entry.topology,
          chunkSize: entry.chunkSize || DEFAULT_CHUNK_SIZE,
        });
      }
    },
    onFeatures: (sequenceId, features) => {
      featureBag.set(sequenceId, features);
    },
    onChunk: async (sequenceId, index, codes) => {
      const list = chunkBag.get(sequenceId) ?? [];
      list.push({ index, codes });
      chunkBag.set(sequenceId, list);
      if (list.length >= 8) {
        await putChunks(stagingId, sequenceId, list.splice(0, list.length));
      }
    },
  });

  for (const [sequenceId, leftover] of chunkBag) {
    if (leftover.length) await putChunks(stagingId, sequenceId, leftover);
  }

  for (const entry of result.manifest.sequences) {
    const extra = pendingMeta.get(entry.id);
    const length = result.lengths[entry.id] ?? entry.length;
    const chunkSize = extra?.chunkSize ?? entry.chunkSize ?? DEFAULT_CHUNK_SIZE;
    await putSequenceMeta({
      projectId: stagingId,
      sequenceId: entry.id,
      name: extra?.name ?? entry.name,
      description: extra?.description ?? '',
      length,
      topology: extra?.topology ?? entry.topology,
      chunkSize,
      chunkCount: Math.ceil(length / chunkSize),
      featureCount: (featureBag.get(entry.id) ?? []).length,
    });
    await putFeatures(stagingId, entry.id, featureBag.get(entry.id) ?? []);
  }

  await promoteImportStaging(projectId, stagingId);
  return result.manifest;
}

export async function saveGprojFile(
  projectId: string,
  projectName: string
): Promise<{ blob: Blob; manifest: GprojManifest }> {
  const sequences = await listSequenceMeta(projectId);
  const parts: Uint8Array[] = [];
  const manifest = await writeGprojStream({
    project: { id: projectId, name: projectName },
    sequences: await Promise.all(
      sequences.map(async (meta) => ({
        id: meta.sequenceId,
        name: meta.name,
        description: meta.description,
        length: meta.length,
        topology: meta.topology,
        chunkSize: meta.chunkSize,
        chunkCount: meta.chunkCount,
        features: await getFeatures(meta.projectId, meta.sequenceId),
        readChunk: async (index: number) => {
          const chunk = await getChunk(meta.projectId, meta.sequenceId, index);
          if (!chunk) throw new Error(`チャンクがありません: ${meta.sequenceId}#${index}`);
          return chunk;
        },
      }))
    ),
    onData: (data) => {
      parts.push(data);
    },
  });
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const joined = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    joined.set(part, offset);
    offset += part.length;
  }
  return { blob: new Blob([joined], { type: 'application/zip' }), manifest };
}

export async function exportSequenceText(
  sequence: { read: (interval: { start: number; end: number }) => Promise<Uint8Array>; length: number; name: string; topology: 'linear' | 'circular' },
  features: GenomicFeatureV1[],
  format: 'fasta' | 'genbank'
): Promise<string> {
  if (sequence.length > 2_000_000) {
    throw new Error('2 Mbp を超える配列は FASTA / GenBank ではなく .gproj で保存してください');
  }
  const codes = await sequence.read({ start: 0, end: sequence.length });
  return format === 'fasta'
    ? formatFastaText(sequence.name, codes)
    : formatGenBankText({ name: sequence.name, codes, topology: sequence.topology, features });
}

export function fileNameFor(name: string, ext: string): string {
  return `${name.replace(/[^\w.-]+/g, '_') || 'sequence'}.${ext}`;
}

export type { StoredSequenceMeta };
